import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAchievementProgress,
  useAchievements,
  type AchievementKind,
} from '@busy/api';
import { ACHIEVEMENT_CATALOG, thresholdLabel } from '../../lib/achievements';
import { KindIcon } from '../CelebrationModal';
import { useT } from '../../lib/i18n';

// "Almost there" nudges. Surfaces the next 1-3 closest un-earned milestones
// across all 7 categories on the Today screen. Computes proximity as
// (current / next_threshold) so a barber with $940 sees "$60 to $1k revenue"
// before "200 cuts away from 250 cuts."
//
// All counts come from `useAchievementProgress()` — a single RPC
// (get_user_achievement_progress, migration 0064) that runs the SAME
// query check_achievements() uses to award. Before 0064 this tile
// approximated some values (clients = roster size, cuts/revenue = highest
// unlocked threshold), which mismatched the DB and showed wrong gaps —
// e.g. a barber with 50 contacts but only 12 real visitors would see
// "you're 38 away from 50 clients" while the DB had them at 12. Single
// source of truth fixes that class of drift outright.

type NextUp = {
  kind: AchievementKind;
  current: number;
  threshold: number;
  remaining: number;
  /** 0..1 — how close to the next threshold; higher = closer. */
  proximity: number;
};

/**
 * The next milestone the user is working TOWARD for this kind. Returns
 * the smallest threshold that satisfies BOTH:
 *   1. t > current — the user hasn't already surpassed it numerically
 *   2. !earned.has(t) — the user hasn't already been awarded it
 *
 * Both conditions, not either. Earlier this function used an OR and
 * had a class of false-positive bug: a user past T1 numerically but
 * missing the T1 row in the achievements table (DB drift, missed
 * trigger, post-import) would see T1 proposed as "next milestone"
 * with a full-looking progress bar — misleading them into thinking
 * work was still needed for something already done.
 *
 * Edge case the AND continues to handle: user earned T2, then
 * edits / archives drop current below T2. With AND, we skip T2 (it's
 * in earned set) and propose T3, even though current < T2. That's
 * intentional — the T2 badge is already won; the user is working on
 * what's next, not re-earning what's done.
 */
function nextThreshold(kind: AchievementKind, current: number, earned: Set<number>): number | null {
  const cat = ACHIEVEMENT_CATALOG[kind];
  for (const t of cat.thresholds) {
    if (t > current && !earned.has(t)) return t;
  }
  return null;
}

export function NextMilestonesTile() {
  const t = useT();
  const navigate = useNavigate();
  const achievements = useAchievements();
  const progress = useAchievementProgress();

  const stats = useMemo(() => {
    // Earned-thresholds index (for nextThreshold's "skip already-earned"
    // pass — without this the function would propose a threshold the
    // user has already unlocked if their count temporarily moves below
    // it after an edit / archive).
    const earnedByKind: Record<AchievementKind, Set<number>> = {
      streak: new Set(),
      clients: new Set(),
      cuts: new Set(),
      revenue: new Set(),
      lessons: new Set(),
      transformations: new Set(),
      timekeeper: new Set(),
    };
    for (const a of achievements.data ?? []) {
      earnedByKind[a.kind].add(a.threshold);
    }

    // All seven kinds come from the RPC — same numbers the DB uses to
    // award. No more approximation, no more drift.
    const p = progress.data;
    return {
      streak:          p?.streak ?? 0,
      clients:         p?.clients ?? 0,
      cuts:            p?.cuts ?? 0,
      revenue:         p?.revenue_cents ?? 0,
      lessons:         p?.lessons ?? 0,
      transformations: p?.transformations ?? 0,
      timekeeper:      p?.timekeeper_hours ?? 0,
      earnedByKind,
    };
  }, [achievements.data, progress.data]);

  const candidates: NextUp[] = useMemo(() => {
    const kinds: AchievementKind[] = ['streak', 'clients', 'cuts', 'revenue', 'lessons', 'transformations', 'timekeeper'];
    const out: NextUp[] = [];
    for (const kind of kinds) {
      const cur = stats[kind] as number;
      const nxt = nextThreshold(kind, cur, stats.earnedByKind[kind]);
      if (nxt == null) continue;
      const remaining = Math.max(0, nxt - cur);
      const proximity = nxt > 0 ? cur / nxt : 0;
      out.push({ kind, current: cur, threshold: nxt, remaining, proximity });
    }
    // Closest to the next milestone first.
    out.sort((a, b) => b.proximity - a.proximity);
    return out;
  }, [stats]);

  if (achievements.loading || progress.loading || candidates.length === 0) return null;

  // Show top 3 — keeps the tile from dominating the page.
  const top = candidates.slice(0, 3);

  // Whole tile is a button — tapping anywhere routes to the achievements
  // gallery. The user explicitly asked for the milestones chip on Today
  // to link to the achievements section, so we make the obvious target
  // (the entire card) the click affordance instead of just adding a
  // smaller "View all" link buried at the corner.
  return (
    <button
      type="button"
      onClick={() => navigate('/achievements')}
      aria-label={t('today.nextUp.openAria')}
      className="w-full text-left rounded-2xl bg-surface border border-line p-4 space-y-3 hover:border-navy-900/30 dark:hover:border-gold-500/30 hover:shadow-sm transition-all"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg leading-tight">{t('today.nextUp.title')}</h2>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-gold-500">
            {t('today.nextUp.eyebrow')}
          </span>
          <ChevronRightIcon className="w-3.5 h-3.5 text-ink-500" />
        </span>
      </div>
      <ul className="space-y-2">
        {top.map((n) => {
          const thr = thresholdLabel(n.kind, n.threshold);
          const goal = t(thr.key, thr.vars);
          const remainingLabel = formatRemaining(n.kind, n.remaining, t);
          return (
            <li
              key={n.kind}
              className="flex items-center gap-3 rounded-xl border border-line bg-bg-alt px-3 py-2.5"
            >
              <div className="w-9 h-9 rounded-lg bg-navy-900/8 dark:bg-gold-500/15 grid place-items-center shrink-0 text-navy-900 dark:text-gold-400">
                <KindIcon kind={n.kind} className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <strong className="block text-sm leading-snug">
                  {t('today.nextUp.gap', { remaining: remainingLabel, goal })}
                </strong>
                <div className="h-1.5 rounded-full bg-line mt-1.5 overflow-hidden">
                  <div
                    className="h-full bg-gold-500 transition-[width] duration-500"
                    style={{ width: `${Math.min(100, Math.round(n.proximity * 100))}%` }}
                    aria-hidden
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </button>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function formatRemaining(
  kind: AchievementKind,
  remaining: number,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (kind === 'revenue') {
    // Cents → dollars, with K formatting for thousands.
    const dollars = remaining / 100;
    if (dollars >= 1000) return '$' + (dollars / 1000).toFixed(dollars % 1000 === 0 ? 0 : 1) + 'K';
    return '$' + Math.round(dollars);
  }
  // streak / cuts / clients / lessons — bare integer with appropriate noun.
  // Plural-aware via i18n keys.
  const isOne = remaining === 1;
  const key = isOne ? `today.nextUp.unit.${kind}_one` : `today.nextUp.unit.${kind}_other`;
  return t(key, { n: remaining });
}
